import * as React from "react";

// Interactive mark-and-sweep GC model, ported from
// design-reference/blog-gc-post.html's `#gcStatus` / `.heap` / `.obj`
// widget. Same interaction shape as the prototype (mark() traces
// reachability outward from roots with a staggered reveal, sweep() fades
// out anything left unmarked, reset() restores the toy heap) — rebuilt as
// a controlled React component so it can be dropped into MDX output via
// the shared component registry instead of living as inline <script>.
//
// Props match packages/shared/src/mdx-components.ts's MDX_COMPONENTS entry:
// `roots` (which object indices are directly reachable "GC roots") and
// `objects` (heap size). Everything else — the adjacency graph, the mark
// animation, the sweep — is derived from those two props.

export interface GCVisualizerProps {
  roots: number[];
  objects: number;
}

type Phase = "idle" | "marking" | "marked" | "swept";

const MARK_STEP_MS = 180;

/**
 * Build a simplified reachability graph over `objects` heap slots, rooted
 * at `roots`. This is the same "simplified adjacency" idea as the
 * reference's hand-written `edges` map (each root reaches a couple of
 * neighboring slots, one of which chains one level deeper), just
 * generalized so it works for any roots/objects pair instead of being
 * hardcoded to 16 objects and roots [0, 5, 11].
 */
function buildEdges(objects: number, roots: number[]): Map<number, number[]> {
  const edges = new Map<number, number[]>();
  for (let i = 0; i < objects; i++) edges.set(i, []);

  const claimed = new Set<number>(roots);

  for (const root of roots) {
    if (root < 0 || root >= objects) continue;
    const children: number[] = [];
    for (const candidate of [root + 1, root + 2]) {
      if (candidate < objects && !claimed.has(candidate)) {
        children.push(candidate);
        claimed.add(candidate);
      }
    }
    edges.set(root, children);

    // Chain one grandchild off the first child, same as the reference's
    // one-level-deeper links (0->1->3, 5->6->8, 11->12->14).
    const grandchild = root + 3;
    if (children.length > 0 && grandchild < objects && !claimed.has(grandchild)) {
      const parent = children[0]!;
      edges.get(parent)!.push(grandchild);
      claimed.add(grandchild);
    }
  }

  return edges;
}

export function GCVisualizer({ roots, objects }: GCVisualizerProps) {
  const edges = React.useMemo(() => buildEdges(objects, roots), [objects, roots]);
  const rootSet = React.useMemo(() => new Set(roots), [roots]);

  const [phase, setPhase] = React.useState<Phase>("idle");
  const [marked, setMarked] = React.useState<Set<number>>(new Set());
  const [swept, setSwept] = React.useState<Set<number>>(new Set());
  const [status, setStatus] = React.useState("idle");

  const timeouts = React.useRef<ReturnType<typeof setTimeout>[]>([]);

  const clearTimeouts = React.useCallback(() => {
    for (const id of timeouts.current) clearTimeout(id);
    timeouts.current = [];
  }, []);

  React.useEffect(() => clearTimeouts, [clearTimeouts]);

  function mark() {
    if (phase !== "idle") return;
    setPhase("marking");
    setStatus("marking reachable objects...");

    const seen = new Set<number>();
    const stack = [...roots];
    let delay = 0;

    while (stack.length) {
      const cur = stack.pop()!;
      if (seen.has(cur)) continue;
      seen.add(cur);

      const id = setTimeout(() => {
        setMarked((prev) => new Set(prev).add(cur));
      }, delay);
      timeouts.current.push(id);
      delay += MARK_STEP_MS;

      for (const next of edges.get(cur) ?? []) stack.push(next);
    }

    const doneId = setTimeout(() => {
      setStatus(`${seen.size} of ${objects} objects reachable`);
      setPhase("marked");
    }, delay + 200);
    timeouts.current.push(doneId);
  }

  function sweep() {
    if (phase !== "marked") return;
    const freed = new Set<number>();
    for (let i = 0; i < objects; i++) {
      if (!marked.has(i)) freed.add(i);
    }
    setSwept(freed);
    setStatus("freed unreachable objects");
    setPhase("swept");
  }

  function reset() {
    clearTimeouts();
    setMarked(new Set());
    setSwept(new Set());
    setStatus("idle");
    setPhase("idle");
  }

  return (
    <div className="gc-widget">
      <div className="gc-widget-label">
        // toy heap — {objects} objects, {roots.length} roots
      </div>

      <div className="heap">
        {Array.from({ length: objects }, (_, i) => (
          <div
            key={i}
            className={
              "obj" +
              (rootSet.has(i) ? " root" : "") +
              (marked.has(i) ? " marked" : "") +
              (swept.has(i) ? " swept" : "")
            }
          >
            obj{i}
          </div>
        ))}
      </div>

      <div className="gc-controls">
        <button type="button" className="primary" disabled={phase !== "idle"} onClick={mark}>
          mark()
        </button>
        <button type="button" disabled={phase !== "marked"} onClick={sweep}>
          sweep()
        </button>
        <button type="button" onClick={reset}>
          reset
        </button>
        <span className="gc-status">{status}</span>
      </div>

      <div className="gc-legend">
        <span>
          <span className="legend-dot" />
          unmarked
        </span>
        <span>
          <span className="legend-dot m" />
          marked (reachable)
        </span>
        <span>● = root</span>
      </div>
    </div>
  );
}

export default GCVisualizer;
